import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { tienePermiso } from "@/lib/permisos"
import { useBrandStore, useAuthStore } from "@/stores"
import { Button } from "@/components/ui"
import { BrandLogo } from "@/components/ui/brand-logo"
import { tipoLabel } from "@/lib/jerarquia"
import { puedeRecargasTerceros } from "@/pages/recargas-externas/access"
import {
  X,
  Home,
  Coins,
  Wallet,
  CardSim,
  Smartphone,
  ShoppingBag,
  User,
  Users,
  FileText,
  Megaphone,
  Sun,
  Moon,
  LogOut,
  Settings,
  DollarSign,
  Shield,
  Zap,
  Mail,
  Wifi,
  IdCard,
  Search,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"

interface NavItem {
  title: string
  href: string
  icon: LucideIcon
  permiso: string
  /** Si está presente, sólo se muestra cuando user.tipo_usuario está en la lista. */
  soloTipos?: string[]
  /** Indent visual: subitem dentro del item anterior. */
  subitem?: boolean
}

// Matriz de pestañas por tipo (PR de permisos por tipo, 2026-05-18).
// La visibilidad combina: permiso del usuario (e/sl/u) + soloTipos (gating
// duro por tipo). El service y endpoints aplican filtros equivalentes.
const navItems: NavItem[] = [
  { title: "Inicio", href: "/index", icon: Home, permiso: "home_all" },
  // M (override) NO tiene tiempo aire: su dinero (comisiones/override) lo ve
  // y lo solicita en "Comisiones". No ve "Saldos".
  { title: "Saldos", href: "/saldos", icon: Wallet, permiso: "saldos_all", soloTipos: ["A", "B", "C", "R"] },
  { title: "Ventas", href: "/ventas", icon: Coins, permiso: "ventas_all", soloTipos: ["Admin", "A", "B", "C", "R"] },
  { title: "Inventario de SIMS", href: "/inventario-de-sims", icon: CardSim, permiso: "inventarios_all", soloTipos: ["Admin", "A", "B", "C"] },
  { title: "Recarga", href: "/recarga", icon: Zap, permiso: "activacion_all", soloTipos: ["B", "C", "R"] },
  { title: "Recargas y Servicios", href: "/recargas-servicios", icon: Zap, permiso: "ventas_all", soloTipos: ["B", "C", "R"] },
  { title: "Portabilidad Activación", href: "/portabilidad-activacion", icon: Smartphone, permiso: "activacion_all", soloTipos: ["B", "C"] },
  { title: "Consultar portabilidad", href: "/consulta-portabilidad", icon: Search, permiso: "activacion_all", soloTipos: ["B", "C"], subitem: true },
  { title: "Vincular titular", href: "/vincular-titular", icon: IdCard, permiso: "activacion_all", soloTipos: ["B", "C"] },
  { title: "Consulta de perfil", href: "/consulta-de-perfil", icon: Wifi, permiso: "consulta_perfil_all", soloTipos: ["B"] },
  { title: "Compras", href: "/compras", icon: ShoppingBag, permiso: "compras_all", soloTipos: ["A", "B", "C", "R"] },
  { title: "Perfil", href: "/perfil", icon: User, permiso: "perfil_all" },
  // M (Master) tampoco ve "Usuarios": sus B los da de alta su A y se los
  // asigna (Usuarios → editar B → Master). Decisión 2026-10-01.
  { title: "Usuarios", href: "/usuarios", icon: Users, permiso: "usuarios_all", soloTipos: ["Admin", "A", "B", "C"] },
  { title: "Perfiles Permisos", href: "/perfiles-permiso", icon: Shield, permiso: "usuarios_all", soloTipos: ["Admin", "A", "B", "C"] },
  { title: "Reportes", href: "/reportes", icon: FileText, permiso: "reportes_all", soloTipos: ["Admin", "A", "M", "B", "C"] },
  { title: "Publicidad y manuales", href: "/publicidad-y-manuales", icon: Megaphone, permiso: "publicidad_y_anuncios_all" },
  { title: "Comisiones", href: "/comisiones", icon: DollarSign, permiso: "comisiones_all", soloTipos: ["Admin", "A", "B", "M"] },
  { title: "SMTP de marca", href: "/admin/smtp-marca", icon: Mail, permiso: "configuracion_all", soloTipos: ["Admin", "A"] },
  { title: "Configuración", href: "/configuracion", icon: Settings, permiso: "configuracion_all", soloTipos: ["Admin"] },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  miniState: boolean
  onMiniStateChange: (mini: boolean) => void
}

export function Sidebar({ isOpen, onClose, miniState, onMiniStateChange }: SidebarProps) {
  const location = useLocation()
  const { config, isDark, toggleDarkMode } = useBrandStore()
  const { user, logout } = useAuthStore()

  // Filtrar menús según permisos + soloTipos (gating por tipo_usuario)
  const filteredItems = navItems.filter((item) => {
    // Recargas de terceros: rollout controlado por allowlist de email (canary).
    if (item.href === "/recargas-servicios") {
      return puedeRecargasTerceros(user)
    }
    if (item.soloTipos && user?.tipo_usuario && !item.soloTipos.includes(user.tipo_usuario)) {
      return false
    }
    if (user?.permisos) {
      return tienePermiso(user.permisos as Record<string, string>, item.permiso)
    }
    return true
  })

  const handleMouseEnter = () => onMiniStateChange(false)
  const handleMouseLeave = () => onMiniStateChange(true)

  const handleNavClick = () => {
    // En móvil, cerrar el sidebar al hacer click en un link
    if (window.innerWidth < 768) {
      onClose()
    }
  }

  // Ancho del sidebar: en desktop mini=64px, expandido=250px. En móvil siempre expandido cuando está abierto
  const sidebarWidth = miniState ? 64 : 250

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}
      
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-screen transition-all duration-300",
          // Estilo CRM claro: fondo neutro + borde sutil. En dark mode usa
          // background oscuro nativo. Sin slate-900 hardcoded.
          "bg-card border-r border-border",
          "max-md:-translate-x-full max-md:w-[260px]",
          isOpen && "max-md:translate-x-0"
        )}
        style={{ width: typeof window !== "undefined" && window.innerWidth >= 768 ? sidebarWidth : 260 }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Header con logo de marca (resuelto por hostname).
            Usa BrandLogo que conoce los paths correctos (/brands/<slug>/logo.png)
            y maneja fallback elegante si el asset falta. */}
        <div className="flex h-16 items-center justify-between border-b border-border px-3">
          {(!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) ? (
            <Link to="/index" className="flex items-center gap-2 min-w-0" onClick={handleNavClick}>
              <BrandLogo
                className="h-10 w-32"
                imgClassName="h-full w-full object-contain object-left"
                fallbackClassName="h-10 w-10 rounded-lg"
                fallbackIcon={
                  <span className="text-base font-bold text-white">
                    {config.name?.charAt(0) || "P"}
                  </span>
                }
              />
            </Link>
          ) : (
            <Link to="/index" className="flex justify-center w-full" onClick={handleNavClick}>
              <BrandLogo
                className="h-8 w-8"
                imgClassName="h-full w-full object-contain"
                fallbackClassName="h-8 w-8 rounded-lg"
                fallbackIcon={
                  <span className="text-xs font-bold text-white">
                    {config.name?.charAt(0) || "P"}
                  </span>
                }
              />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="md:hidden shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Bloque de usuario (solo expandido) */}
        {(!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && user && (
          <div className="flex items-center gap-2.5 px-3 py-3 border-b border-border">
            <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0 ring-2 ring-primary/20">
              <span className="text-sm font-semibold text-primary">
                {(user.nombre || "U").charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-tight truncate" title={user.nombre || ""}>
                {user.nombre || "Usuario"}
              </p>
              <p className="text-[11px] text-muted-foreground leading-tight truncate">
                {tipoLabel(user.tipo_usuario)}
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex flex-col gap-0.5 p-2 overflow-y-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
          {(!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && (
            <div className="px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Menú</span>
            </div>
          )}
          {filteredItems.map((item) => {
            const isActive = location.pathname === item.href || (item.href === "/index" && location.pathname === "/dashboard")
            const Icon = item.icon
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleNavClick}
                className={cn(
                  "group relative flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors",
                  item.subitem ? "pl-10 pr-3" : "px-3",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/70 hover:bg-muted hover:text-foreground"
                )}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-primary" />
                )}
                <Icon className={cn("h-5 w-5 shrink-0", isActive ? "text-primary" : "text-muted-foreground")} />
                {(!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && (
                  <span className="truncate">
                    {/* Para el C, Usuarios son sólo sus empleados */}
                    {item.href === "/usuarios" && user?.tipo_usuario === "C" ? "Empleados" : item.title}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-border p-2 space-y-0.5 bg-card">
          <Button
            variant="ghost"
            size={miniState && typeof window !== "undefined" && window.innerWidth >= 768 ? "icon" : "default"}
            onClick={toggleDarkMode}
            className={cn(
              "w-full text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg",
              (!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && "justify-start gap-3"
            )}
          >
            {isDark ? <Sun className="h-5 w-5 shrink-0" /> : <Moon className="h-5 w-5 shrink-0" />}
            {(!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && (
              <span>{isDark ? "Modo claro" : "Modo oscuro"}</span>
            )}
          </Button>
          <Button
            variant="ghost"
            size={miniState && typeof window !== "undefined" && window.innerWidth >= 768 ? "icon" : "default"}
            onClick={() => { logout(); handleNavClick(); }}
            className={cn(
              "w-full text-destructive/80 hover:text-destructive hover:bg-destructive/10 rounded-lg",
              (!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && "justify-start gap-3"
            )}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {(!miniState || (typeof window !== "undefined" && window.innerWidth < 768)) && (
              <span>Cerrar sesión</span>
            )}
          </Button>
        </div>
      </aside>
    </>
  )
}

// Exportar el ancho del sidebar para que MainLayout pueda usarlo
export const SIDEBAR_WIDTH_MINI = 64
export const SIDEBAR_WIDTH_EXPANDED = 250
